// Engineer PWA push-notification opt-in nudge (Session AFF), rebuilt by Session PN1 (22 October 2026) so an engineer ALWAYS hears about
// new work. Measured on production that day: of 5 active engineer logins only 1 had a phone registered, because the old card was
// hidden for a WEEK after "Not now", said nothing at all on an iPhone that had not added the app to its Home Screen, and nothing
// brought a lost registration back.
//
// WHAT IT DOES NOW, in order:
//   - A phone that cannot do web push AND is an iPhone not yet added to the Home Screen is told how (two steps), because that is
//     the only way an iPhone can ever receive one. Any other unsupported browser sees nothing.
//   - Permission already granted: it re-registers the phone SILENTLY on every open (ensurePushRegistered), so a cleared browser, a
//     reinstall, a changed key or a row the server dropped never leaves an engineer unreachable. It shows nothing.
//   - Permission blocked: it says plainly how to turn it back on in the phone's settings.
//   - Permission not yet asked: it asks, and "Not now" hides the card for THIS visit only (sessionStorage), not for a week, so it
//     comes back until notifications are on.
// The registration belongs to the DEVICE and the engineer, not to the session: a session that runs out never removes it.
//
// It renders nothing when the server has no VAPID keys (GET /api/push/status configured:false). The service worker's
// notification-click navigation hint is owned by EngineerLayout (Session ANZ), not here.
import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import {
  isPushSupported,
  isIos,
  isStandalone,
  getExistingSubscription,
  subscribeToPush,
  unsubscribePush,
  reconcilePushPermission,
  ensurePushRegistered,
  pushPermission,
  isPushOptedOut,
  setPushOptOut,
} from "../../lib/pushClient";

// "Not now" lasts for this visit only. Window-guarded and try/catch-swallowed (private mode / quota safe).
const SESSION_DISMISS_KEY = "ele.engineerPushNudge.notNowThisVisit";

function dismissedThisVisit(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(SESSION_DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function recordDismissedThisVisit(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
  } catch {
    /* best effort */
  }
}

type Mode = "none" | "ask" | "blocked" | "ios_install";

export default function PushBanner() {
  const [mode, setMode] = useState<Mode>("none");
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isPushSupported()) {
        // An iPhone only does web push for an app on the Home Screen. In Safari's own tab there is no PushManager, so say how.
        if (isIos() && !isStandalone() && !dismissedThisVisit()) setMode("ios_install");
        return;
      }
      // Session ANZ: reconcile FIRST. A permission turned off in the phone's settings leaves a stored subscription the server would
      // go on counting as a reachable device; clearing it keeps the server's device count honest.
      const reconciled = await reconcilePushPermission();
      if (cancelled) return;
      let status;
      try {
        status = await api.push.status();
      } catch {
        return;
      }
      if (cancelled || !status.configured || !status.publicKey) return;
      setPublicKey(status.publicKey);
      // An engineer who turned notifications Off on purpose (the Home row or the sign-out tick) is not nagged and not silently turned back on:
      // the On/Off row on Home is where they chose it and where they turn it on again.
      if (isPushOptedOut()) return;
      const permission = pushPermission();
      if (permission === "granted") {
        // Silent: no prompt, no card. Idempotent, and it re-homes the phone to whoever is signed in now.
        await ensurePushRegistered(status.publicKey);
        return;
      }
      if (dismissedThisVisit()) return;
      if (permission === "denied") {
        setMode("blocked");
        return;
      }
      // "default": offered again every visit until it is on. (A permission reset to default leaves no subscription.)
      const existing = reconciled === "revoked_cleared" ? null : await getExistingSubscription();
      if (cancelled || existing) return;
      setMode("ask");
    })().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const enable = useCallback(async () => {
    if (!publicKey || busy) return;
    setBusy(true);
    const result = await subscribeToPush(publicKey);
    setBusy(false);
    if (result.ok) setMode("none");
    else if (result.reason === "denied") setMode("blocked");
    else setMode("ask");
  }, [publicKey, busy]);

  const notNow = useCallback(() => {
    recordDismissedThisVisit();
    setMode("none");
  }, []);

  if (mode === "none") return null;

  if (mode === "ios_install") {
    return (
      <div className="rounded-ele border border-ele-card-border bg-ele-card p-4" role="region" aria-label="Notifications">
        <p className="text-sm text-ele-strong">To get job alerts on your iPhone, add this app to your Home Screen first.</p>
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-ele-body">
          <li>Tap the Share button at the bottom of Safari.</li>
          <li>Tap Add to Home Screen, then open the app from your Home Screen and turn notifications on.</li>
        </ol>
        <button type="button" onClick={notNow} className="mt-3 min-h-11 text-sm text-ele-muted underline-offset-2 hover:underline">
          Not now
        </button>
      </div>
    );
  }

  if (mode === "blocked") {
    return (
      <div className="rounded-ele border border-ele-card-border bg-ele-card p-4" role="region" aria-label="Notifications">
        <p className="text-sm text-ele-strong">Notifications are blocked on this phone, so new jobs will not reach you.</p>
        <p className="mt-1 text-sm text-ele-body">Turn them on for this app in your phone&rsquo;s settings, then open the app again.</p>
        <button type="button" onClick={notNow} className="mt-3 min-h-11 text-sm text-ele-muted underline-offset-2 hover:underline">
          Not now
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-ele border border-ele-card-border bg-ele-card p-4" role="region" aria-label="Notifications">
      <p className="text-sm text-ele-strong">Turn on notifications so new jobs reach you, even when you are signed out.</p>
      <div className="mt-3 flex items-center gap-4">
        <button
          type="button"
          onClick={() => void enable()}
          disabled={busy}
          className="min-h-11 rounded-ele bg-[#63C41F] px-4 font-menu text-sm font-semibold tracking-wide text-ele-bg transition disabled:opacity-40"
        >
          {busy ? "Enabling..." : "Enable"}
        </button>
        <button type="button" onClick={notNow} className="min-h-11 text-sm text-ele-muted underline-offset-2 hover:underline">
          Not now
        </button>
      </div>
    </div>
  );
}

// The engineer's own setting: On or Off with ONE control (Session PN1). It sits beside Sign out on the Home screen, quietly. Off
// here is the engineer's deliberate choice (an explicit unsubscribe); the card above handles the "turn it on" nudge.
export function PushStatusRow() {
  const [state, setState] = useState<"loading" | "hidden" | "on" | "off">("loading");
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isPushSupported()) return setState("hidden");
      let status;
      try {
        status = await api.push.status();
      } catch {
        return setState("hidden");
      }
      if (cancelled) return;
      if (!status.configured || !status.publicKey) return setState("hidden");
      setPublicKey(status.publicKey);
      const existing = pushPermission() === "granted" ? await getExistingSubscription() : null;
      if (!cancelled) setState(existing ? "on" : "off");
    })().catch(() => setState("hidden"));
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    if (state === "on") {
      setPushOptOut(true); // before the removal, so a re-open mid-way never re-subscribes
      await unsubscribePush();
      setState("off");
    } else if (publicKey) {
      const r = await subscribeToPush(publicKey);
      setState(r.ok ? "on" : "off");
    }
    setBusy(false);
  }, [busy, state, publicKey]);

  if (state === "loading" || state === "hidden") return null;
  return (
    <p className="text-sm text-ele-body" data-testid="push-status-row">
      Notifications: <span className="font-heading text-ele-strong">{state === "on" ? "On" : "Off"}</span>{" "}
      <button type="button" onClick={() => void toggle()} disabled={busy} className="min-h-11 px-2 text-ele-green underline-offset-2 hover:underline disabled:opacity-40">
        {state === "on" ? "Turn off" : "Turn on"}
      </button>
    </p>
  );
}
